// 訂單詳情: 產生發票 / 關聯PO / 利潤
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, auth, money, fmtDate, todayStr, addDays, ORDER_STATUS, INV_STATUS, PO_STATUS, r2 } from '../api.js';
import { Modal, Field, StatusBadge, CategoryTag, Loading, PageHead, useToast, useConfirm } from '../ui.jsx';

export default function OrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { toast } = useToast();
  const [confirm, confirmNode] = useConfirm();
  const [o, setO] = useState(null);
  const [terms, setTerms] = useState([]);
  const [settings, setSettings] = useState({});
  const [gen, setGen] = useState(null); // generate invoices modal
  const [pl, setPl] = useState(null); // profit-loss
  const [costItems, setCostItems] = useState(null);
  const [addCost, setAddCost] = useState(null); // {category, description, amount}
  const [costSources, setCostSources] = useState(null); // ⑦ 未入數嘅分判商單/供應商發票
  const loadPL = () => {
    api.get('/orders/' + id + '/profit-loss').then(setPl).catch(() => {});
    api.get('/orders/' + id + '/costs').then(setCostItems).catch(() => setCostItems([]));
  };
  useEffect(() => { loadPL(); }, [id]);
  const uploadReceipt = (cid, file) => {
    const rd = new FileReader();
    rd.onload = async () => {
      try { await api.post('/project-costs/' + cid + '/receipt', { mime: file.type, data_base64: rd.result }); toast('單據已上傳 ✔'); loadPL(); }
      catch (e) { toast(e.message, 'err'); }
    };
    rd.readAsDataURL(file);
  };
  const refresh = () => api.get(`/orders/${id}`).then(setO);
  useEffect(() => {
    refresh();
    api.get('/payment-terms').then(setTerms);
    api.get('/settings').then(setSettings);
  }, [id]);
  if (!o) return <Loading />;

  const invoiced = r2(o.invoices.filter(i => i.status !== 'void').reduce((s, i) => s + i.adjusted_amount, 0));
  const hasInvoices = o.invoices.some(i => i.status !== 'void');
  const received = r2(o.invoices.reduce((s, i) => s + i.paid_amount, 0));
  const poTotal = r2(o.purchase_orders.filter(p => p.status !== 'cancelled').reduce((s, p) => s + p.total, 0));
  const profit = r2(invoiced - poTotal);

  const openGen = () => {
    const term = terms.find(t => t.id === Number(o.payment_term_id));
    const insts = (term?.installments || []).map((ins, i, arr) => {
      const pct = Number(ins.percent) || 0;
      const amount = i === arr.length - 1 ? r2(o.total - arr.slice(0, -1).reduce((s, x) => s + o.total * (Number(x.percent) || 0) / 100, 0)) : r2(o.total * pct / 100);
      return { label: ins.label || `第 ${i + 1} 期`, amount, issue_date: todayStr(), due_date: ins.due_days ? addDays(todayStr(), Number(ins.due_days)) : todayStr() };
    });
    if (!insts.length) insts.push({ label: '全數', amount: o.total, issue_date: todayStr(), due_date: addDays(todayStr(), 30) });
    setGen({ installments: insts, term_id: o.payment_term_id || '' });
  };
  const genWithTerm = (tid) => {
    const term = terms.find(t => t.id === Number(tid));
    setGen(g => {
      if (!term) return g;
      const insts = term.installments.map((ins, i, arr) => {
        const amount = i === arr.length - 1 ? r2(o.total - arr.slice(0, -1).reduce((s, x) => s + o.total * (Number(x.percent) || 0) / 100, 0)) : r2(o.total * (Number(ins.percent) || 0) / 100);
        return { label: ins.label || `第 ${i + 1} 期`, amount, issue_date: todayStr(), due_date: ins.due_days ? addDays(todayStr(), Number(ins.due_days)) : todayStr() };
      });
      return { ...g, installments: insts, term_id: tid };
    });
  };
  const doGen = async () => {
    try {
      const r = await api.post(`/orders/${id}/generate-invoices`, { installments: gen.installments });
      toast(`已產生 ${r.created.length} 張發票 ✔`);
      setGen(null); refresh();
    } catch (e) { toast(e.message, 'err'); }
  };
  const submitInv = async (inv) => { try { await api.post(`/invoices/${inv.id}/submit`); toast(`${inv.invoice_no} 已標記發送 ✔`); refresh(); } catch (e) { toast(e.message, 'err'); } };
  const setStatus = async (status) => { try { await api.put(`/orders/${id}`, { status }); toast('狀態已更新'); refresh(); } catch (e) { toast(e.message, 'err'); } };
  const toPDF = async () => {
    try {
      toast('正在產生 PDF…');
      const resp = await fetch('/api/orders/' + id + '/generate-pdf', { headers: { Authorization: 'Bearer ' + auth.token } });
      if (!resp.ok) throw new Error('PDF 生成失敗 (' + resp.status + ')');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = o.order_no + '.pdf'; a.click();
      URL.revokeObjectURL(url);
      toast('PDF 已下載 ✔');
    } catch (e) { toast(e.message, 'err'); }
  };

  // ⑥ Variation Order — 工程進行中加項目, 獨立編號獨立開票
  const addVO = () => confirm(`開一張 Variation Order (VO) 掛喺 ${o.order_no} 下面？\nVO 有自己編號 (${o.order_no}-V?) 可以獨立開票。`, async () => {
    try { const r = await api.post(`/orders/${id}/variation`, {}); toast('已開 VO: ' + r.order_no + ' ✔'); nav('/orders/' + r.id); }
    catch (e) { toast(e.message, 'err'); }
  }, { okLabel: '開 VO' });

  return (
    <div>
      <PageHead icon="🏗️" title={`${o.order_no} — ${o.title || '工程訂單'}`}
        desc={`${o.customer_name} · ${fmtDate(o.order_date)} ${o.quote_no ? `· 來自報價 ${o.quote_no}` : ''}`}
        actions={<>
          <button className="btn" onClick={() => nav('/orders')}>← 返回</button>
          <select className="select" style={{ width: 130 }} value={o.status} onChange={e => setStatus(e.target.value)}>
            {Object.entries(ORDER_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <button className="btn" onClick={toPDF}>📥 PDF</button>
          <button className="btn amber" onClick={addVO} title="工程進行中加項目 — 獨立編號獨立開票">➕ VO</button>
          {hasInvoices
            ? <button className="btn primary" onClick={() => toast('此訂單已開出發票 — 如需加收項目請開 VO (➕ VO 掣)，或先作廢原有發票', 'err')}>🧾 已開發票</button>
            : <button className="btn primary" onClick={openGen}>🧾 產生發票</button>}
        </>} />

      <div className="stat-grid">
        <div className="stat-card"><span className="ico-badge">📝</span><div>
          <div className="stat-val"><span className="cur">HK$</span>{money(o.total)}</div><div className="stat-label">合約總額</div><div className="stat-hint">{o.payment_term_name || '未設付款條款'}</div></div></div>
        <div className="stat-card"><span className="ico-badge sky">🧾</span><div>
          <div className="stat-val"><span className="cur">HK$</span>{money(invoiced)}</div><div className="stat-label">已開出發票</div><div className="stat-hint">已收 {money(received)}</div></div></div>
        <div className="stat-card"><span className="ico-badge amber">📦</span><div>
          <div className="stat-val"><span className="cur">HK$</span>{money(poTotal)}</div><div className="stat-label">分判成本 (PO)</div><div className="stat-hint">{o.purchase_orders.length} 張分判商單</div></div></div>
        <div className="stat-card"><span className={`ico-badge ${profit >= 0 ? 'mint' : 'coral'}`}>{profit >= 0 ? '📈' : '📉'}</span><div>
          <div className="stat-val" style={{ color: profit >= 0 ? 'var(--mint)' : 'var(--coral)' }}><span className="cur">HK$</span>{money(profit)}</div>
          <div className="stat-label">工程利潤 (開票 − PO)</div>
          <div className="stat-hint">{o.total > 0 ? `毛利 ${Math.round(profit / (invoiced || o.total) * 1000) / 10}%` : ''}</div></div></div>
      </div>

      <div className="card">
        <div className="card-title"><span className="ico-badge mint" style={{ width: 28, height: 28, fontSize: 13, borderRadius: 8 }}>📋</span>工程項目明細 <CategoryTag value={o.category} />
          <span className="spacer" />
          {o.address ? <span className="st st-gray">📍 {o.address_label}: {o.address}</span> : null}
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr><th>#</th><th>描述</th><th className="num">數量</th><th>單位</th><th className="num">單價</th><th className="num">金額</th></tr></thead>
            <tbody>
              {o.items.map((it, i) => (
                <tr key={it.id}><td className="muted">{i + 1}</td><td className="strong">{it.description}</td>
                  <td className="num">{it.qty}</td><td>{it.unit}</td>
                  <td className="num">{money(it.unit_price)}</td><td className="num strong">{money(it.amount)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="total-bar">
          <div className="line"><span>小計</span><span className="v">HK$ {money(o.subtotal)}</span></div>
          {o.discount_type !== 'none' && o.discount_value > 0 ? <div className="line">
            <span>折扣 ({o.discount_type === 'percent' ? o.discount_value + '%' : '固定'})</span><span className="v">- HK$ {money(o.subtotal - o.total)}</span></div> : null}
          <div className="line grand"><span>合約總額</span><span className="v">HK$ {money(o.total)}</span></div>
        </div>
        {o.remarks?.length ? (
          <div className="doc-remarks mt" style={{ marginBottom: 0 }}>
            <div className="label">REMARKS 備註條款</div>
            <ul>{o.remarks.map((r, i) => <li key={i}>{r}</li>)}</ul>
          </div>
        ) : null}
      </div>

      {/* ===== PROJECT RUNNING COST SHEET (損益表) ===== */}
      <div className="card mt">
        <div className="card-title"><span className="ico-badge mint" style={{ width: 28, height: 28, fontSize: 13, borderRadius: 8 }}>📊</span>Project Running Cost Sheet — 損益表
          <span className="spacer" />
          <button className="btn sm primary" onClick={() => { setAddCost({ category: 'vendor', description: '', amount: '' }); api.get('/orders/' + id + '/cost-sources').then(setCostSources).catch(() => setCostSources([])); }}>➕ Add Cost Item</button>
        </div>
        {pl ? (
          <>
            <div className="tbl-wrap">
              <table className="tbl">
                <thead><tr><th>Category</th><th className="num">Budget 預算</th><th className="num">Actual 實際</th><th className="num">% of Budget</th></tr></thead>
                <tbody>
                  {pl.rows.map((r2i, i) => (
                    <tr key={i}>
                      <td className="strong" style={{ textTransform: 'capitalize' }}>{r2i.category === 'misc' ? 'T&E / Misc.' : r2i.category}</td>
                      <td className="num">{money(r2i.budget)}</td>
                      <td className="num">{money(r2i.actual)}</td>
                      <td className="num"><span className={'st ' + (r2i.budget > 0 && r2i.actual > r2i.budget ? 'st-red' : 'st-green')}>{r2i.pct}%</span></td>
                    </tr>
                  ))}
                  <tr style={{ background: 'var(--surface-soft)', fontWeight: 800 }}>
                    <td>Total Cost</td>
                    <td className="num">{money(pl.total_budget)}</td>
                    <td className="num">{money(pl.total_actual)}</td>
                    <td></td>
                  </tr>
                  <tr style={{ fontWeight: 800 }}>
                    <td>Running Margin (合約額 − 實際成本)</td>
                    <td className="num" colSpan={2} style={{ color: pl.margin_amount >= 0 ? 'var(--mint)' : 'var(--coral)', fontSize: 15 }}>
                      HK$ {money(pl.margin_amount)}{pl.margin_pct !== null ? ' (' + pl.margin_pct + '%)' : ''}
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="muted mt" style={{ marginBottom: 6 }}>實際成本明細 ({(costItems || []).length} 項) — 每項可上傳收據 PDF/相片:</p>
            {(costItems || []).map(c => (
              <div key={c.id} className="row" style={{ justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px dashed var(--line)', fontSize: 13 }}>
                <span><span className="count-pill">{c.category}</span> <strong>{c.description || '—'}</strong></span>
                <span className="row" style={{ flexWrap: 'nowrap' }}>
                  <strong>HK$ {money(c.amount)}</strong>
                  {c.receipt_file
                    ? <a className="btn sm" href={'/api/project-costs/' + c.id + '/receipt'}>📎 單據</a>
                    : <label className="btn sm" style={{ cursor: 'pointer' }}>📸 上傳單據
                        <input type="file" accept="application/pdf,image/*" style={{ display: 'none' }}
                          onChange={e => { const f = e.target.files[0]; e.target.value = ''; if (f) uploadReceipt(c.id, f); }} />
                      </label>}
                  <button className="btn sm coral" onClick={async () => { try { await api.del('/project-costs/' + c.id); toast('已刪除'); loadPL(); } catch (e) { toast(e.message, 'err'); } }}>✕</button>
                </span>
              </div>
            ))}
            {(costItems || []).length === 0 ? <p className="muted" style={{ padding: '8px 4px' }}>未有實際成本紀錄 — 撳「➕ Add Cost Item」入帳</p> : null}
          </>
        ) : <Loading />}
      </div>

      {addCost && (
        <Modal onClose={() => setAddCost(null)} icon="🧾" title="➕ Add Cost Item — 成本入帳"
          footer={<><button className="btn" onClick={() => setAddCost(null)}>取消</button>
            <button className="btn primary" onClick={async () => {
              if (!(Number(addCost.amount) > 0)) { toast('請填金額', 'err'); return; }
              try { await api.post('/orders/' + id + '/costs', addCost); toast('成本已入帳 ✔'); setAddCost(null); loadPL(); }
              catch (e) { toast(e.message, 'err'); }
            }}>💾 儲存</button></>}>
          <div className="form-grid">
            <div className="field"><label>Category 類別</label>
              <select className="select" value={addCost.category} onChange={e => setAddCost({ ...addCost, category: e.target.value })}>
                <option value="labour">Labour 人工</option>
                <option value="vendor">Vendor 供應商</option>
                <option value="subcontractor">Subcontractor 分判</option>
                <option value="material">Material / Delivery 材料</option>
                <option value="misc">T&E / Misc. 雜項</option>
              </select>
            </div>
            <div className="field"><label>Amount 金額 HKD$</label><input className="input num-input" type="number" step="0.01" value={addCost.amount} onChange={e => setAddCost({ ...addCost, amount: e.target.value })} /></div>
            <div className="field" style={{ gridColumn: '1 / -1' }}><label>Description 描述</label><input className="input" value={addCost.description} onChange={e => setAddCost({ ...addCost, description: e.target.value })} /></div>
          </div>
          {/* ⑦ 搵返之前入咗嘅分判商單/供應商發票 — 一撳直接填入, 避免重複入數 */}
          {(costSources || []).length > 0 && (
            <div style={{ marginTop: 10, border: '1px dashed var(--line)', borderRadius: 10, padding: 10 }}>
              <div className="strong" style={{ fontSize: 13, marginBottom: 6 }}>📋 揀返現有嘅單直接入 (未入過數嘅)</div>
              <div style={{ maxHeight: 150, overflowY: 'auto' }}>
                {costSources.map(cs => (
                  <div key={cs.type + cs.id} className="row" style={{ justifyContent: 'space-between', padding: '5px 4px', borderBottom: '1px dashed var(--line)', cursor: 'pointer' }}
                    onClick={() => setAddCost({ ...addCost, category: cs.type === 'po' ? 'subcontractor' : 'vendor', description: (cs.type === 'po' ? '分判商單 ' : '供應商發票 ') + cs.label + ' — ' + (cs.supplier || ''), amount: cs.type === 'po' ? (cs.si_total > 0 ? cs.si_total : cs.amount) : cs.amount, source_type: cs.type, source_id: cs.id })}>
                    <span>[{cs.type === 'po' ? '分判商單' : '供應商發票'}] <strong>{cs.label}</strong> — {cs.supplier}</span>
                    <span className="strong">HK$ {money(cs.type === 'po' ? (cs.si_total > 0 ? cs.si_total : cs.amount) : cs.amount)}</span>
                  </div>
                ))}
              </div>
              <p className="muted" style={{ marginTop: 6, fontSize: 12 }}>⭐ 分判商單有供應商發票嘅話會自動用發票總額。入過數嘅單唔會再出現。</p>
            </div>
          )}
          <p className="muted mt">儲存後喺明細行可以上傳收據 (PDF / 相片) 存檔。</p>
        </Modal>
      )}

      <div className="grid2 mt">
        <div className="card">
          <div className="card-title"><span className="ico-badge sky" style={{ width: 28, height: 28, fontSize: 13, borderRadius: 8 }}>🧾</span>發票 ({o.invoices.length})<span className="spacer" />
            {hasInvoices
              ? <button className="btn sm primary" onClick={() => toast('此訂單已開出發票 — 如需加收項目請開 VO (➕ VO 掣)', 'err')}>➕ 已開發票</button>
              : <button className="btn sm primary" onClick={openGen}>➕ 產生發票</button>}</div>
          {o.invoices.length === 0 ? <div className="muted" style={{ padding: '16px 4px' }}>未開出任何發票 — 按「產生發票」根據付款條款自動分期開票</div> : (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead><tr><th>發票</th><th>分期</th><th>到期</th><th className="num">金額</th><th className="num">已收</th><th>狀態</th><th></th></tr></thead>
                <tbody>
                  {o.invoices.map(inv => (
                    <tr key={inv.id}>
                      <td className="strong">{inv.invoice_no}<div className="sub">{fmtDate(inv.issue_date)}</div></td>
                      <td>{inv.installment_label}</td>
                      <td>{fmtDate(inv.due_date)}</td>
                      <td className="num strong">{money(inv.adjusted_amount)}</td>
                      <td className="num">{money(inv.paid_amount)}</td>
                      <td><StatusBadge map={INV_STATUS} value={inv.status} /></td>
                      <td>{inv.status === 'pending' ? <button className="btn sm amber" onClick={() => submitInv(inv)}>📤 發送</button> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-title"><span className="ico-badge amber" style={{ width: 28, height: 28, fontSize: 13, borderRadius: 8 }}>📦</span>掛單採購 ({o.purchase_orders.length})<span className="spacer" />
            <button className="btn sm primary" onClick={() => nav(`/pos?order=${id}`)}>➕ 開分判商單</button></div>
          {o.purchase_orders.length === 0 ? <div className="muted" style={{ padding: '16px 4px' }}>此工程未有分判商單</div> : (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead><tr><th>PO</th><th>供應商</th><th className="num">金額</th><th>狀態</th></tr></thead>
                <tbody>
                  {o.purchase_orders.map(p => (
                    <tr key={p.id} className="clickable" onClick={() => nav('/pos')}>
                      <td className="strong">{p.po_no}<div className="sub">{fmtDate(p.po_date)}{p.attachment_count ? ' · 📎' + p.attachment_count : ''}</div></td>
                      <td>{p.supplier_name}</td>
                      <td className="num strong">{money(p.total)}</td>
                      <td><StatusBadge map={PO_STATUS} value={p.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {o.payments.length ? (
            <div className="mt">
              <div className="card-title" style={{ marginBottom: 8 }}><span className="ico-badge mint" style={{ width: 28, height: 28, fontSize: 13, borderRadius: 8 }}>✅</span>收款紀錄</div>
              {o.payments.map(p => (
                <div key={p.id} className="row" style={{ justifyContent: 'space-between', fontSize: 12.5, padding: '6px 4px', borderBottom: '1px dashed var(--line)' }}>
                  <span>{fmtDate(p.pay_date)} · {p.invoice_no}</span><strong>+ HK$ {money(p.amount)}</strong>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {gen && (
        <Modal size="lg" onClose={() => setGen(null)} icon="🧾" title={`產生發票 — ${o.order_no}`}
          footer={<><button className="btn" onClick={() => setGen(null)}>取消</button><button className="btn primary" onClick={doGen}>🧾 確認開票</button></>}>
          <Field label="以付款條款重新計算">
            <select className="select" value={gen.term_id} onChange={e => genWithTerm(e.target.value)}>
              <option value="">— 手動 —</option>
              {terms.map(t => <option key={t.id} value={t.id}>{t.name} — {t.description}</option>)}
            </select>
          </Field>
          <p className="muted mt">每期金額同日期都可以修改；總和會開出 {gen.installments.length} 張發票。</p>
          <div className="tbl-wrap mt">
            <table className="tbl items-table">
              <thead><tr><th>分期說明</th><th style={{ width: 130 }}>開出日期</th><th style={{ width: 130 }}>到期日</th><th style={{ width: 130 }}>金額</th></tr></thead>
              <tbody>
                {gen.installments.map((ins, i) => {
                  const upd = (k, v) => setGen(g => ({ ...g, installments: g.installments.map((x, xi) => xi === i ? { ...x, [k]: v } : x) }));
                  return (
                    <tr key={i}>
                      <td><input className="input" value={ins.label} onChange={e => upd('label', e.target.value)} /></td>
                      <td><input className="input" type="date" value={ins.issue_date} onChange={e => upd('issue_date', e.target.value)} /></td>
                      <td><input className="input" type="date" value={ins.due_date} onChange={e => upd('due_date', e.target.value)} /></td>
                      <td><input className="input num-input" type="number" step="0.01" value={ins.amount} onChange={e => upd('amount', e.target.value)} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="total-bar">
            <div className="line"><span>合約總額</span><span className="v">HK$ {money(o.total)}</span></div>
            <div className="line"><span>本次開票合計</span><span className="v">HK$ {money(gen.installments.reduce((s, x) => s + (Number(x.amount) || 0), 0))}</span></div>
          </div>
        </Modal>
      )}
      {confirmNode}
    </div>
  );
}
